import React from "react";
import { getElementDescendant } from "../../helpers/utils";
import { referenceFrameLabel } from "../../constants/orbitMetadata";
import { ConjunctionField } from "./ConjunctionField";
import { ConjunctionObjectTitle } from "./ConjunctionObjectTitle";
import {
  getFormattedDisplayValue,
  getScientificDisplayValue,
} from "./conjunctionUiUtils";
import { getObjectRoleLabel } from "../../utils/objectRoleLabels";

interface ConjunctionElementProps {
  conjunction: unknown;
  orbitElement: "target" | "chaser";
  formatters: unknown;
}

type OrbitView = "keplerian" | "stateVector";

const getElementTitle = (element: "target" | "chaser") =>
  `${getObjectRoleLabel(element)} Object`;

const getObjectValue = (
  conjunction: unknown,
  orbitElement: "target" | "chaser",
  path: string[],
) => getElementDescendant(conjunction, [orbitElement, ...path]);

export const ConjunctionElement: React.FC<ConjunctionElementProps> = ({
  conjunction,
  orbitElement,
  formatters,
}) => {
  const [orbitView, setOrbitView] = React.useState<OrbitView>("keplerian");

  const keplerianFields = [
    {
      label: "Arg Perigee",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}ArgPerigee.value`,
        formatters,
      ),
    },
    {
      label: "Ascending Node",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}AscendingNode.value`,
        formatters,
      ),
    },
    {
      label: "Eccentricity",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}Eccentricity.value`,
        formatters,
      ),
    },
    {
      label: "Inclination",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}Inclination.value`,
        formatters,
      ),
    },
    {
      label: "Semimajor Axis",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}SemimajorAxis.value`,
        formatters,
      ),
    },
    {
      label: "True Anomaly",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}TrueAnomaly.value`,
        formatters,
      ),
    },
  ];

  const stateVectorFields = [
    {
      label: "X",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}X.value`,
        formatters,
      ),
    },
    {
      label: "Y",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}Y.value`,
        formatters,
      ),
    },
    {
      label: "Z",
      value: getFormattedDisplayValue(
        conjunction,
        `${orbitElement}Z.value`,
        formatters,
      ),
    },
    {
      label: "Vx",
      value: getScientificDisplayValue(conjunction, `${orbitElement}Vx.value`),
    },
    {
      label: "Vy",
      value: getScientificDisplayValue(conjunction, `${orbitElement}Vy.value`),
    },
    {
      label: "Vz",
      value: getScientificDisplayValue(conjunction, `${orbitElement}Vz.value`),
    },
  ];

  const orbitFields =
    orbitView === "keplerian" ? keplerianFields : stateVectorFields;

  const orbitTabs: Array<{ id: OrbitView; title: string }> = [
    { id: "keplerian", title: "Keplerian" },
    { id: "stateVector", title: "State vector" },
  ];

  const covarianceValues = {
    nn: getFormattedDisplayValue(
      conjunction,
      `${orbitElement}CovNN.value`,
      formatters,
    ),
    rn: getFormattedDisplayValue(
      conjunction,
      `${orbitElement}CovRN.value`,
      formatters,
    ),
    rr: getFormattedDisplayValue(
      conjunction,
      `${orbitElement}CovRR.value`,
      formatters,
    ),
    rt: getFormattedDisplayValue(
      conjunction,
      `${orbitElement}CovRT.value`,
      formatters,
    ),
    tn: getFormattedDisplayValue(
      conjunction,
      `${orbitElement}CovTN.value`,
      formatters,
    ),
    tt: getFormattedDisplayValue(
      conjunction,
      `${orbitElement}CovTT.value`,
      formatters,
    ),
  };

  const covarianceMatrix = [
    {
      axis: "R",
      values: [covarianceValues.rr, covarianceValues.rt, covarianceValues.rn],
    },
    {
      axis: "T",
      values: [covarianceValues.rt, covarianceValues.tt, covarianceValues.tn],
    },
    {
      axis: "N",
      values: [covarianceValues.rn, covarianceValues.tn, covarianceValues.nn],
    },
  ];

  return (
    <article className="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="shrink-0 border-b border-slate-200 bg-slate-50 px-5 py-4">
        <p className="mb-2 text-xs font-semibold uppercase text-slate-500">
          {getElementTitle(orbitElement)}
        </p>
        <ConjunctionObjectTitle
          objectId={getObjectValue(conjunction, orbitElement, [
            "spaceObject",
            "id",
          ])}
          name={getObjectValue(conjunction, orbitElement, [
            "spaceObject",
            "name",
          ])}
          norad={getObjectValue(conjunction, orbitElement, [
            "spaceObject",
            "noradId",
          ])}
          cospar={getObjectValue(conjunction, orbitElement, [
            "spaceObject",
            "cosparId",
          ])}
          sourceOfInformation={getObjectValue(conjunction, orbitElement, [
            "sourceOfInformation",
          ])}
          updateTime={getElementDescendant(conjunction, [
            `${orbitElement}OrbitUpdateTime`,
          ])}
        />
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-5">
        <section>
          <div className="mb-2 flex min-w-0 flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="text-xs font-semibold uppercase text-slate-500">
                Orbit
              </h3>
              <span
                className="truncate text-xs font-medium text-slate-500"
                title={referenceFrameLabel}
              >
                {referenceFrameLabel}
              </span>
            </div>
            <div className="inline-flex min-w-max items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
              {orbitTabs.map((tab) => {
                const isSelected = orbitView === tab.id;

                return (
                  <button
                    className={`cursor-pointer rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                      isSelected
                        ? "focusoc-theme-surface shadow-sm"
                        : "text-slate-600 hover:bg-white hover:text-slate-950"
                    }`}
                    key={tab.id}
                    onClick={() => setOrbitView(tab.id)}
                    type="button"
                  >
                    {tab.title}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
            {orbitFields.map((field) => (
              <ConjunctionField
                key={field.label}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase text-slate-500">
            Covariance
          </h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <div className="min-w-[420px]">
              <div className="grid grid-cols-[2.5rem_repeat(3,minmax(7rem,1fr))] border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                <div className="px-3 py-2" />
                {["R", "T", "N"].map((axis) => (
                  <div className="px-3 py-2 text-center" key={axis}>
                    {axis}
                  </div>
                ))}
              </div>

              {covarianceMatrix.map((row) => (
                <div
                  className="grid grid-cols-[2.5rem_repeat(3,minmax(7rem,1fr))] border-b border-slate-100 last:border-b-0"
                  key={row.axis}
                >
                  <div className="bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-slate-500">
                    {row.axis}
                  </div>
                  {row.values.map((value, index) => (
                    <div
                      className="min-w-0 border-l border-slate-100 px-3 py-2 text-center text-sm font-medium text-slate-900"
                      key={`${row.axis}-${index}`}
                      title={value}
                    >
                      <span className="whitespace-nowrap">{value}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </article>
  );
};
